"use client"

import {
  Clock,
  FileSpreadsheet,
  RefreshCcw,
  AlertTriangle,
  Database,
  Workflow,
} from "lucide-react"
import { useInView } from "@/hooks/use-in-view"
import { cn } from "@/lib/utils"

const problems = [
  {
    icon: Clock,
    borderClass: "border-border-gris/25 hover:border-icon-hover-red/80 hover:shadow-lg shadow-red-500/40",
    iconClass: "text-titulo-hero/40 group-hover:text-icon-hover-red/80",
    title: "Tareas repetitivas que consumen tiempo",
    description:
      "¿Tu equipo pasa horas en tareas manuales que podrían automatizarse? Identificamos y eliminamos los cuellos de botella.",
  },
  {
    icon: FileSpreadsheet,
    borderClass: "border-border-gris/25 hover:border-icon-hover-yellow/80 hover:shadow-lg shadow-yellow-500/40",
    iconClass: "text-titulo-hero/40 group-hover:text-icon-hover-yellow/80",
    title: "Hojas de cálculo inmanejables",
    description:
      "Múltiples archivos Excel, versiones desactualizadas y errores de copia. Centralizamos tu información en aplicaciones inteligentes.",
  },
  {
    icon: RefreshCcw,
    borderClass: "border-border-gris/25 hover:border-icon-hover-green/80 hover:shadow-lg shadow-green-500/40",
    iconClass: "text-titulo-hero/40 group-hover:text-icon-hover-green/80",
    title: "Procesos manuales propensos a errores",
    description:
      "La entrada manual de datos genera inconsistencias. Automatizamos la validación y el procesamiento de información.",
  },
  {
    icon: AlertTriangle,
    borderClass: "border-border-gris/25 hover:border-icon-hover-cian/80 hover:shadow-lg shadow-cyan-500/40", 
    iconClass: "text-titulo-hero/40 group-hover:text-icon-hover-cian/80",
    title: "Falta de visibilidad en tiempo real",
    description:
      "¿Necesitas informes que tardan días en generarse? Creamos dashboards en tiempo real para tomar mejores decisiones.",
  },
  {
    icon: Database,
    borderClass: "border-border-gris/25 hover:border-icon-hover-purple/80 hover:shadow-lg shadow-purple-500/40",
    iconClass: "text-titulo-hero/40 group-hover:text-icon-hover-purple/80",
    title: "Información dispersa en múltiples sistemas",
    description:
      "Datos fragmentados dificultan el análisis. Integramos tus fuentes de información en una única plataforma.",
  },
  {
    icon: Workflow,
    borderClass: "border-border-gris/25 hover:border-icon-hover-white/80 hover:shadow-lg shadow-gray-500/40",
    iconClass: "text-titulo-hero/40 group-hover:text-icon-hover-white/80",
    title: "Flujos de trabajo desconectados",
    description:
      "Procesos que requieren intervención manual entre pasos. Creamos flujos automatizados de principio a fin.",
  },
]

export function Problems() {
  const { ref: titleRef, isInView: titleInView } =
    useInView<HTMLHeadingElement>()

  return (
    <section id="soluciones" className="py-16 lg:py-24 bg-background-problems">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {/* Header */}
        <div className="mx-auto max-w-2xl text-center">
          <h2
            ref={titleRef}
            className={cn(
              "mt-4 font-work text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-titulo-hero transition-all duration-1000 ease-out will-change-transform",
              "motion-reduce:transition-none motion-reduce:transform-none",
              titleInView
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-3",
            )}
          >
            ¿Te identificas con alguno de estos desafíos?
          </h2>
          <p
            className={cn(
              "font-jetbrains font-extralight mt-3 text-lg text-titulo-hero/60 transition-all duration-1000 ease-out will-change-transform",
              "motion-reduce:transition-none motion-reduce:transform-none",
              titleInView
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-3",
            )}
          >
            Estos son los problemas más comunes que enfrentan nuestros clientes antes de trabajar con nosotros.
          </p>
        </div>

        {/* Problems grid */}
        <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {problems.map((problem, index) => (
            <div
              key={index}
              className={`group relative p-6 rounded-xl bg-background-problems border transition-all duration-300 ${problem.borderClass}`}
            >
              <div className={`flex items-center justify-center font-display transition-colors ${problem.iconClass}`}>
                <problem.icon className="h-12 w-12" stroke-width="0.8" />
              </div>
              <h3 className="mt-6 font-work text-lg font-semibold text-titulo-hero/80">
                {problem.title}
              </h3>
              <p className="mt-3 font-jetbrains font-thin text-titulo-hero/60 leading-relaxed text-base">
                {problem.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
